<script lang="ts">
const ORIGINAL_WIDTH = 100
const ORIGINAL_HEIGHT = 20
const ARROW_HEIGHT = 10
const VB_PAD = 1
const CLIP_PAD = 16
</script>
<script setup lang="ts">
defineProps({
  tooltipHeight: null,
  tooltipWidth: null,
  selected: { type: Boolean }
})

// DO NOT CHANGE






/**
 * The way this works is we take our figma representation of the tooltip object, and we
 * replace the variables that stand for the height and width parts, and we displace them
 * by the difference between the original width and height and the new width and height.
 */
const d = computed(() => {
  // offset from original center to new center
  const c = (__props.tooltipWidth - ORIGINAL_WIDTH) / 2
  const r = __props.tooltipWidth - ORIGINAL_WIDTH
  const b = __props.tooltipHeight - ORIGINAL_HEIGHT
  return [
    // top middle bend
    `M ${c + 48.2832} 1.04767`,
    `C ${c + 49.4392} -0.363385 ${c + 50.6566} -0.33499 ${c + 51.7168} 1.04767`,
    `L ${c + 55.1621} 4.90412`,
    `C ${c + 56.8419} 6.78451 ${c + 57.6819} 7.72484 ${c + 58.6621} 8.39728`,
    `C ${c + 59.5313} 8.99351 ${c + 60.4795} 9.43265 ${c + 61.4707} 9.69904`,
    `C ${c + 62.538} 9.98583 ${c + 63.6691} 9.99826 ${c + 65.8369} 9.99884`,
    // top right corner
    `H ${r + 93.5996}`,
    `C ${r + 95.8398} 9.99884 ${r + 96.9608} 9.99939 ${r + 97.8164} 10.4354`,
    `C ${r + 98.5688} 10.8189 ${r + 99.181} 11.4309 ${r + 99.5645} 12.1834`,
    `C ${r + 100} 13.039 ${r + 100} 14.1593 ${r + 100} 16.3992`,
    // bottom right corner
    `V ${b + 23.5994}`,
    `C ${r + 100} ${b + 25.8394} ${r + 100} ${b + 26.9596} ${r + 99.5645} ${b + 27.8152}`,
    `C ${r + 99.181} ${b + 28.5678} ${r + 98.5689} ${b + 29.1798} ${r + 97.8164} ${b + 29.5633}`,
    `C ${r + 96.9608} ${b + 29.9993} ${r + 95.8398} ${b + 29.9988} ${r + 93.5996} ${b + 29.9988}`,
    // bottom left corner
    `H6.40039`,
    `C4.16018 ${b + 29.9988} 3.03924 ${b + 29.9993} 2.18359 ${b + 29.5633}`,
    `C1.43108 ${b + 29.1798} 0.818991 ${b + 28.5678} 0.435547 ${b + 27.8152}`,
    `C-0.000385815 ${b + 26.9596} 2.10575e-09 ${b + 25.8394} 1.19977e-09 ${b + 23.5994}`,
    // top left corner
    `V16.3992`,
    `C1.19977e-09 14.1593 -0.000236766 13.039 0.435547 12.1834`,
    `C0.818962 11.4309 1.43116 10.8189 2.18359 10.4354`,
    `C3.03924 9.99939 4.16018 9.99884 6.40039 9.99884`,
    // top middle bend
    `H ${c + 34.1631}`,
    `C ${c + 36.3309} 9.99826 ${c + 37.462} 9.98583 ${c + 38.5293} 9.69904`,
    `C ${c + 39.5205} 9.43265 ${c + 40.4687} 8.99351 ${c + 41.3379} 8.39728`,
    `C ${c + 42.3181} 7.72484 ${c + 43.1581} 6.78451 ${c + 44.8379} 4.90412`,
    `L ${c + 48.2832} 1.04767Z`,
  ].join(" ")
})
const totalHeight = computed(() => __props.tooltipHeight + ARROW_HEIGHT)
const id = useId()
</script>

<template>
  <div
    class="tooltip-container"
    :style="{ width: px(tooltipWidth), height: px(tooltipHeight) }"
  >
    <svg
      :width="tooltipWidth + 2 * VB_PAD"
      :height="totalHeight + 2 * VB_PAD"
      :viewBox="`-${VB_PAD} -${VB_PAD} ${tooltipWidth + 2 * VB_PAD} ${totalHeight + 2 * VB_PAD}`"
      fill="none"
      class="tooltip-svg"
      :style="{ top: px(-ARROW_HEIGHT - 1), left: px(0) }"
    >
      <foreignObject
        :x="-CLIP_PAD"
        :y="-CLIP_PAD"
        :width="tooltipWidth + CLIP_PAD * 2"
        :height="totalHeight + CLIP_PAD * 2"
      >
        <div class="tooltip-blur" :style="{ clipPath: `url(#${id})` }" />
      </foreignObject>
      <path
        :d
        class="tooltip-bg"
        :class="{ selected }"
      />
      <defs>
        <clipPath :id :transform="`translate(${CLIP_PAD} ${CLIP_PAD})`">
          <path :d />
        </clipPath>
      </defs>
    </svg>
    <div class="tooltip-content" :style="{ width: px(tooltipWidth), height: px(tooltipHeight) }">
      <slot />
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.tooltip-container
  @apply relative

.tooltip-content
  @apply absolute overflow-hidden
  @apply left-0 top-[0px] // seems to be an OBOE somewhere
  @apply rounded-[#{$page-snippets-br-snippet}]
  @apply p-0.25 // don't intersect the stroke
.tooltip-svg
  @apply absolute

.tooltip-bg
  @apply fill-[#{$page-snippets-bg-snippet}] stroke-[#{$page-snippets-b-snippet}] stroke-1.5
  &.selected
    @apply stroke-$global-blue-secondary
.tooltip-blur
  @apply h-full w-full
  @apply backdrop-blur-[6px] backdrop-filter // don't use a heavy blur
</style>
